import React, { useState, useEffect } from 'react';
import { baseConfig } from '../config/settings';
import tokenABI from '../abi/ADBYTES.json';
import stakingABI from '../abi/ADBYTESStaking.json';
import Toast from './Toast';
import ContractAddressRow from './ContractAddressRow';
import {
  formatTokens,
  formatPercentFromBps,
  fromWei,
  percentOfSupply,
  burnedFromMaxSupply,
  MAX_SUPPLY,
} from '../utils/format';
import { readWithFailover, getWalletProvider } from '../utils/web3';

const ONCHAIN_POLL_MS = 30000;
const EXPLORER = 'https://basescan.org';

const TokenStats = () => {
  const [tokenSymbol, setTokenSymbol] = useState('');
  const [totalSupply, setTotalSupply] = useState('');
  const [stakingAddress] = useState(baseConfig.stakingContractAddress);
  const [stakedTokenAddress, setStakedTokenAddress] = useState('');
  const [totalStaked, setTotalStaked] = useState(null);
  const [stakedPercent, setStakedPercent] = useState(null);
  const [networkApy, setNetworkApy] = useState(null);
  const [stakingLive, setStakingLive] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [toastMessage, setToastMessage] = useState('');
  const [toastVisible, setToastVisible] = useState(false);

  useEffect(() => {
    let pollingInterval;

    const loadOnChainStats = async () => {
      try {
        const data = await readWithFailover(async (readWeb3) => {
          const tokenContract = new readWeb3.eth.Contract(
            tokenABI,
            baseConfig.contractAddress
          );
          const stakingContract = new readWeb3.eth.Contract(stakingABI, stakingAddress);

          const [symbol, supplyWei, stats, stakedAddr] = await Promise.all([
            tokenContract.methods.symbol().call(),
            tokenContract.methods.totalSupply().call(),
            stakingContract.methods.getStats().call(),
            stakingContract.methods.stakedToken().call(),
          ]);

          const supply = fromWei(supplyWei, readWeb3);
          const staked = fromWei(stats.totalStaked, readWeb3);
          return {
            symbol,
            supply,
            staked,
            stakedAddr,
            apyBps: stats.estimatedAPYBps,
            stakedPercent: percentOfSupply(staked, supply),
          };
        });

        setTokenSymbol(data.symbol);
        setTotalSupply(data.supply);
        setStakingLive(true);
        setStakedTokenAddress(data.stakedAddr);
        setTotalStaked(data.staked);
        setNetworkApy(data.apyBps);
        setStakedPercent(data.stakedPercent);
        setLoadError('');
      } catch (error) {
        console.error('TokenStats load failed:', error);
        setLoadError('Could not refresh token stats.');
      }
    };

    const pollIfVisible = () => {
      if (!document.hidden) loadOnChainStats();
    };

    loadOnChainStats();
    pollingInterval = setInterval(pollIfVisible, ONCHAIN_POLL_MS);
    document.addEventListener('visibilitychange', pollIfVisible);
    return () => {
      clearInterval(pollingInterval);
      document.removeEventListener('visibilitychange', pollIfVisible);
    };
  }, [stakingAddress]);

  const addTokenToWallet = async () => {
    try {
      const walletProvider = getWalletProvider();
      if (walletProvider) {
        await walletProvider.request({
          method: 'wallet_watchAsset',
          params: {
            type: 'ERC20',
            options: {
              address: baseConfig.contractAddress,
              symbol: tokenSymbol,
              decimals: 18,
              image: null,
            },
          },
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleCopy = () => {
    setToastMessage('Copied to clipboard');
    setToastVisible(true);
    setTimeout(() => setToastVisible(false), 2000);
  };

  const uniswapLink = `https://app.uniswap.org/swap?chain=base&inputCurrency=${baseConfig.usdcAddress}&outputCurrency=${baseConfig.contractAddress}`;

  return (
    <div className="bg-white shadow-xl rounded-2xl p-6 border border-gray-100">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-gray-900">Token Info</h3>
        <span className="px-3 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full">
          {baseConfig.chainName}
        </span>
      </div>

      {loadError && (
        <div className="mb-4 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
          {loadError}
        </div>
      )}

      <div className="space-y-3 mb-6">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Symbol</span>
          <span className="text-lg font-bold text-gray-900">{tokenSymbol}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Max Supply</span>
          <span className="text-lg font-bold text-gray-900">{formatTokens(MAX_SUPPLY, 0)}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Current Supply</span>
          <span className="text-lg font-bold text-gray-900">{formatTokens(totalSupply)}</span>
        </div>
        {totalSupply !== '' && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Burned</span>
            <span className="text-lg font-bold text-orange-600">
              {formatTokens(burnedFromMaxSupply(totalSupply))}
            </span>
          </div>
        )}
        <p className="text-xs text-gray-500 pt-1">
          Burned tokens are permanently removed when redeemed for ad credits on the platform.
        </p>
        {stakingLive && totalStaked !== null && (
          <div className="border-t border-gray-100 pt-3 mt-3 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Total Staked</span>
              <span className="text-lg font-bold text-gray-900">{formatTokens(totalStaked)}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">% of Supply</span>
              <span className="text-lg font-bold text-green-600">
                {stakedPercent !== null ? `${stakedPercent.toFixed(2)}%` : '—'}
              </span>
            </div>
            {networkApy !== null && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Staking APY</span>
                <span className="text-lg font-bold text-green-600">
                  {formatPercentFromBps(networkApy)}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 mb-6">
        <button
          type="button"
          className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-4 py-3 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all font-semibold text-sm shadow-md hover:shadow-lg"
          onClick={addTokenToWallet}
        >
          Add to Wallet
        </button>
        <a
          href={uniswapLink}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-3 rounded-lg hover:from-green-600 hover:to-green-700 transition-all font-semibold text-sm text-center shadow-md hover:shadow-lg"
        >
          Swap on Uniswap
        </a>
      </div>

      <div className="space-y-2">
        <div className="text-xs font-semibold text-gray-600 mb-1">Contract Addresses</div>
        <ContractAddressRow
          label="ADBYTES"
          address={baseConfig.contractAddress}
          explorerUrl={`${EXPLORER}/token/${baseConfig.contractAddress}`}
          onCopy={handleCopy}
        />
        {stakingLive && (
          <ContractAddressRow
            label="Staking Vault"
            address={stakingAddress}
            explorerUrl={`${EXPLORER}/address/${stakingAddress}`}
            onCopy={handleCopy}
          />
        )}
        {stakingLive && stakedTokenAddress && (
          <ContractAddressRow
            label="stADBYTES"
            address={stakedTokenAddress}
            explorerUrl={`${EXPLORER}/token/${stakedTokenAddress}`}
            onCopy={handleCopy}
          />
        )}
      </div>

      <Toast message={toastMessage} visible={toastVisible} />
    </div>
  );
};

export default TokenStats;
